/**
 * A thin typed wrapper over the global injected by telegram-web-app.js.
 *
 * No SDK dependency: the surface this app actually touches is small, and a
 * wrapper we own is the only place that has to know the API is untyped and
 * partially missing on older clients.
 */

type ColorScheme = "light" | "dark";

type ThemeParams = {
  bg_color?: string;
  text_color?: string;
  secondary_bg_color?: string;
};

type BackButton = {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
};

type HapticFeedback = {
  impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
  notificationOccurred(type: "error" | "success" | "warning"): void;
  selectionChanged(): void;
};

export type TelegramWebApp = {
  initData: string;
  initDataUnsafe: { user?: { id: number; first_name?: string; username?: string } };
  version: string;
  platform: string;
  colorScheme: ColorScheme;
  themeParams: ThemeParams;
  viewportHeight: number;
  viewportStableHeight: number;
  isExpanded: boolean;
  safeAreaInset?: { top: number; bottom: number; left: number; right: number };
  contentSafeAreaInset?: { top: number; bottom: number; left: number; right: number };
  BackButton: BackButton;
  HapticFeedback: HapticFeedback;
  ready(): void;
  expand(): void;
  close(): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
  setHeaderColor(color: string): void;
  setBackgroundColor(color: string): void;
  setBottomBarColor?(color: string): void;
  enableClosingConfirmation(): void;
  disableClosingConfirmation(): void;
  disableVerticalSwipes?(): void;
  requestFullscreen?(): void;
  showPopup?(params: unknown, cb?: (id: string) => void): void;
  openLink(url: string, options?: { try_instant_view?: boolean }): void;
  openTelegramLink(url: string): void;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg: TelegramWebApp | undefined = window.Telegram?.WebApp;

/** True when running inside a real Telegram client. */
export const insideTelegram = Boolean(tg?.initData);

/**
 * The credential for every API call. Outside Telegram we fall back to a real
 * signed payload captured from a phone, so the server's HMAC check is exactly
 * as strict — this only makes the app drivable in a desktop browser.
 */
export function getInitData(): string {
  return tg?.initData || (import.meta.env.VITE_DEV_INIT_DATA ?? "");
}

/** `x.y` version compare, because older clients silently lack newer methods. */
function atLeast(version: string): boolean {
  if (!tg?.version) return false;
  const [a = 0, b = 0] = tg.version.split(".").map(Number);
  const [x = 0, y = 0] = version.split(".").map(Number);
  return a > x || (a === x && b >= y);
}

function readToken(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** Mirror Telegram's light/dark onto the attribute tokens.css keys dark off. */
function prefersDark(): boolean {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function applyColorScheme(): void {
  const dark = insideTelegram ? tg?.colorScheme === "dark" : prefersDark();
  document.documentElement.dataset.theme = dark ? "dark" : "light";
  // Read after the attribute flips so we paint the chrome in the new palette.
  const bg = readToken("--bg");
  if (!bg) return;
  try {
    tg?.setHeaderColor(bg);
    tg?.setBackgroundColor(bg);
    tg?.setBottomBarColor?.(readToken("--surface") || bg);
  } catch {
    // Older clients reject non-keyword colors. The in-page palette still holds.
  }
}

let started = false;

/** Idempotent boot. Safe to call before React renders. */
export function initTelegram(): void {
  if (started) return;
  started = true;

  window.matchMedia?.("(prefers-color-scheme: dark)").addEventListener("change", applyColorScheme);

  if (!tg) {
    applyColorScheme();
    return;
  }

  tg.ready();
  tg.expand();
  // A vertical drag inside a long list must scroll it, not close the app.
  if (atLeast("7.7")) tg.disableVerticalSwipes?.();

  applyColorScheme();
  tg.onEvent("themeChanged", applyColorScheme);
}

export function setClosingConfirmation(enabled: boolean): void {
  // Bot API 6.2. Older clients warn on every call rather than ignoring it.
  if (!tg || !atLeast("6.2")) return;
  if (enabled) tg.enableClosingConfirmation();
  else tg.disableClosingConfirmation();
}

/**
 * Opens a file in the system browser.
 *
 * `window.open` is unreliable inside the Telegram WebView — the client is what
 * owns navigation, so it has to be asked.
 */
export function openExternal(url: string): void {
  if (tg?.openLink) tg.openLink(url);
  else window.open(url, "_blank", "noopener,noreferrer");
}

/** t.me / tg: links must use openTelegramLink — openLink sends them through an external browser. */
export function openTelegram(url: string): void {
  if (tg?.openTelegramLink && (/^https?:\/\/t\.me\//i.test(url) || url.startsWith("tg:"))) {
    tg.openTelegramLink(url);
    return;
  }
  openExternal(url);
}

/** Opens Telegram's share sheet when inside the client; falls back to a t.me link. */
export function shareToChat(url: string, text: string): void {
  const share = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
  openTelegram(share);
}
